/**
 * useConversationHistory Hook
 * 
 * Custom React hook for managing conversation history data with real-time updates,
 * caching, and comprehensive error handling.
 * 
 * Features:
 * - Automatic data fetching and caching
 * - Real-time updates with polling
 * - Loading states and error handling
 * - Customer profile integration
 * - Message timeline management
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import * as Types from '../types'
import { conversationHistoryService, ConversationUtils } from '../services/conversationHistory'
import { logger } from '../utils/logger'
import { useConversationUpdates } from './useRealTimeNotifications'

// Type aliases to fix module resolution issues
type ConversationHistory = Types.ConversationHistory
type CustomerProfile = Types.CustomerProfile

export interface UseConversationHistoryState {
  // Data state
  conversation: ConversationHistory | null
  customerProfile: CustomerProfile | null
  
  // UI state
  isLoading: boolean
  isLoadingProfile: boolean
  error: string | null
  
  // Computed values
  deviceInfo: string
  locationInfo: string
  marketingInfo: string
  conversationDuration: number
  
  // Actions
  refetch: () => Promise<void>
  loadCustomerProfile: () => Promise<void>
  clear: () => void
}

export interface UseConversationHistoryOptions {
  conversationId?: string
  autoFetch?: boolean
  pollInterval?: number
  includeProfile?: boolean
}

/**
 * Hook for managing conversation history data
 * 
 * @param options Configuration options
 * @returns State and actions for conversation history
 */
export function useConversationHistory(
  options: UseConversationHistoryOptions = {}
): UseConversationHistoryState {
  
  const {
    conversationId,
    autoFetch = true,
    pollInterval = 0, // Disabled - use WebSocket only
    includeProfile = true
  } = options
  
  // State management
  const [conversation, setConversation] = useState<ConversationHistory | null>(null)
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Refs for cleanup and polling
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const mountedRef = useRef(true)
  
  /**
   * Fetch conversation history from API
   */
  const fetchConversationHistory = useCallback(async (): Promise<void> => {
    if (!conversationId) {
      logger.debug('No conversation ID provided, skipping fetch')
      return
    }
    
    try {
      setIsLoading(true)
      setError(null)
      
      logger.info(`🔄 Fetching conversation history: ${conversationId}`)
      
      const history = await conversationHistoryService.getConversationHistory(conversationId)
      
      logger.debug('📊 Raw conversation history data:', {
        hasHistory: !!history,
        totalMessages: history?.total_messages,
        hasMessages: !!history?.messages,
        messagesLength: history?.messages?.length,
        hasProfile: !!history?.customer_profile
      })
      
      setConversation(history)
      
      // Auto-load customer profile if enabled and available
      if (includeProfile && history.customer_profile) {
        setCustomerProfile(history.customer_profile)
      } else if (includeProfile) {
        setCustomerProfile(null)
      }
      
      logger.info(`✅ Conversation history loaded: ${history?.total_messages || 0} messages`)
      logger.debug('📊 Conversation state updated:', {
        hasConversation: !!history,
        messageCount: history?.messages?.length,
        hasProfile: !!history?.customer_profile
      })
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load conversation'
      logger.error('❌ Error fetching conversation history:', err)
      
      setError(errorMessage)
      setConversation(null)
      setCustomerProfile(null)
      
    } finally {
      logger.debug('🏁 Setting isLoading to false')
      setIsLoading(false)
    }
  }, [conversationId, includeProfile])
  
  /**
   * Load customer profile from existing conversation data
   */
  const loadCustomerProfile = useCallback(async (): Promise<void> => {
    if (!conversationId || !conversation) {
      logger.debug('No conversation ID or conversation data for customer profile')
      return
    }
    
    try {
      setIsLoadingProfile(true)
      
      logger.info(`👤 Loading customer profile from conversation: ${conversationId}`)
      
      // Use the customer profile from the conversation history if available
      if (conversation.customer_profile) {
        setCustomerProfile(conversation.customer_profile)
        logger.info(`✅ Customer profile loaded for ${conversation.customer_profile.visitor_name || 'Anonymous'}`)
      } else {
        logger.warn('No customer profile available in conversation data')
      }
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load customer profile'
      logger.error('❌ Error loading customer profile:', err)
      
    } finally {
      if (mountedRef.current) {
        setIsLoadingProfile(false)
      }
    }
  }, [conversationId, conversation])
  
  /**
   * Clear all data and reset state
   */
  const clear = useCallback((): void => {
    logger.debug('🧹 Clearing conversation history state')
    
    setConversation(null)
    setCustomerProfile(null)
    setError(null)
    setIsLoading(false)
    setIsLoadingProfile(false)
    
    // Clear polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }, [])
  
  /**
   * WebSocket-only real-time updates - no polling
   */
  useEffect(() => {
    if (!conversationId) return
    
    logger.debug(`🔌 Setting up WebSocket updates for conversation ${conversationId}`)
    
    // All real-time updates now come through WebSocket events
    // No more polling - pure event-driven architecture
    
    return () => {
      logger.debug(`🧹 Cleaning up WebSocket updates for conversation ${conversationId}`)
    }
  }, [conversationId])
  
  /**
   * Initial data fetch
   */
  useEffect(() => {
    logger.debug('🔄 Auto-fetch effect triggered', {
      autoFetch,
      hasConversationId: !!conversationId,
      conversationId
    })
    
    if (autoFetch && conversationId) {
      logger.debug(`🚀 Auto-fetching conversation history: ${conversationId}`)
      fetchConversationHistory()
    }
  }, [autoFetch, conversationId, fetchConversationHistory])
  
  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      mountedRef.current = false
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [])
  
  // Computed values for UI display
  const profile = customerProfile || conversation?.customer_profile || null
  
  const deviceInfo = profile ? ConversationUtils.formatDeviceInfo(profile) : 'Unknown Device'
  const locationInfo = profile ? ConversationUtils.formatLocationInfo(profile) : 'Unknown Location'
  const marketingInfo = profile ? ConversationUtils.formatMarketingInfo(profile) : 'Direct'
  const conversationDuration = conversation ? 
    ConversationUtils.calculateConversationDuration(conversation.messages) : 0
  
  return {
    // Data state
    conversation,
    customerProfile,
    
    // UI state
    isLoading,
    isLoadingProfile,
    error,
    
    // Computed values
    deviceInfo,
    locationInfo,
    marketingInfo,
    conversationDuration,
    
    // Actions
    refetch: fetchConversationHistory,
    loadCustomerProfile,
    clear
  }
}

/**
 * Hook for managing conversation list with real-time updates
 */
export interface ConversationListFilters {
  status?: string;
  priority?: string;
}

export function useConversationList(pollInterval: number = 10000, filters?: ConversationListFilters) {
  const [conversations, setConversations] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stats, setStats] = useState({ total: 0, pending: 0, assigned: 0 })
  
  const { conversations: conversationUpdates } = useConversationUpdates()
  
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const mountedRef = useRef(true)
  
  const fetchConversations = useCallback(async () => {
    try {
      setError(null)
      setIsLoading(true)
      
      logger.info('🔄 Fetching conversation assignments...', { filters })
      const assignments = await conversationHistoryService.getConversationAssignments(filters)
      
      logger.info(`🔄 Setting conversations state: ${assignments.length} items`)
      setConversations(assignments)
      
      const stats = {
        total: assignments.length,
        pending: assignments.filter(c => c.status === 'pending').length,
        assigned: assignments.filter(c => c.status === 'assigned').length
      }
      setStats(stats)
      
      logger.info(`✅ Updated stats: ${stats.total} total, ${stats.pending} pending, ${stats.assigned} assigned`)
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load conversations'
      logger.error('❌ Error fetching conversation list:', err)
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }, [filters])
  
  // Setup polling
  useEffect(() => {
    // Initial fetch only - all updates come via WebSocket
    fetchConversations()
    
    // WebSocket handles all real-time updates - no more polling
    logger.debug('📡 Conversation list using WebSocket-only updates')
    
  }, [fetchConversations])

  // Listen for new messages and update conversations state in real-time
  useEffect(() => {
    if (conversationUpdates.size > 0) {
      logger.info('📡 New message received via WebSocket in useConversationList', { 
        updateCount: conversationUpdates.size 
      })

      setConversations(prevConversations => {
        let updated = false
        const newConversations = prevConversations.map(conv => {
          const updates = conversationUpdates.get(conv.conversation_id)
          if (updates) {
            updated = true
            logger.debug('🔄 Updating conversation in sidebar with new message', {
              conversationId: conv.conversation_id,
              newLastMessage: updates.last_message,
              oldLastMessage: conv.last_message
            })

            // Create a new object for the updated conversation to ensure React detects the change
            return {
              ...conv,
              last_message: updates.last_message,
              last_message_time: updates.last_message_time,
              updated_at: updates.updated_at
            }
          }
          return conv
        })

        // If no conversation was updated, it means the message was for a conversation not in the current list
        // In that case, we might want to trigger a full refetch if the message is for a new conversation
        // or a conversation that just became relevant. For now, we rely on the poll for that.
        if (!updated) {
          logger.debug('No matching conversation found for new message in current list. Relying on poll/refetch.')
        }

        return newConversations
      })
    }
  }, [conversationUpdates]) // Depend on conversationUpdates to re-run when new messages arrive
  
  return {
    conversations,
    stats,
    isLoading,
    error,
    refetch: fetchConversations
  }
}